import { UploadIcon } from 'lucide-react';
import * as React from 'react';
import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { cn } from '../utils';
import { Button } from './button';

// Context to share dropzone config + selected files between subcomponents
type DropzoneContextType = {
  src?: File[];
  accept?: DropzoneOptions['accept'];
  maxSize?: DropzoneOptions['maxSize'];
  minSize?: DropzoneOptions['minSize'];
  maxFiles?: DropzoneOptions['maxFiles'];
};

const DropzoneContext = React.createContext<DropzoneContextType | undefined>(undefined);

export const useDropzoneContext = () => {
  const ctx = React.useContext(DropzoneContext);
  if (!ctx) {
    throw new Error('useDropzoneContext must be used within <Dropzone>');
  }

  return ctx;
};

export type DropzoneProps = Omit<DropzoneOptions, 'onDrop'> & {
  src?: File[];
  className?: string;
  children?: React.ReactNode;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
  onError?: (error: Error) => void;
};

const renderBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)}${units[unitIndex]}`;
};

export const Dropzone: React.FC<DropzoneProps> = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  children,
  ...rest
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    disabled,
    onDrop(acceptedFiles, fileRejections, event) {
      if (fileRejections.length > 0) {
        const message = fileRejections[0]?.errors[0]?.message || 'File rejected';
        onError?.(new Error(message));
        return;
      }

      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...rest,
  });

  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src?.map(f => f.name))}
      value={{ src, accept, maxSize, minSize, maxFiles }}
    >
      <Button
        type='button'
        variant='outline'
        disabled={disabled}
        className={cn(
          'relative flex h-auto w-full flex-col overflow-hidden p-8 transition-colors',
          isDragActive && 'outline-none ring-1 ring-ring',
          className,
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} disabled={disabled} />
        {children}
      </Button>
    </DropzoneContext.Provider>
  );
};

export type DropzoneEmptyStateProps = {
  children?: React.ReactNode;
  className?: string;
};

export const DropzoneEmptyState: React.FC<DropzoneEmptyStateProps> = ({ children, className }) => {
  const { src, accept, maxSize, minSize, maxFiles } = useDropzoneContext();
  if (src && src.length > 0) {
    return null;
  }

  if (children) {
    return children;
  } // custom render override

  let caption = '';
  if (accept && Object.keys(accept).length) {
    caption += 'Accepts ' + new Intl.ListFormat('en').format(Object.keys(accept));
  }

  if (minSize && maxSize) {
    caption += `${caption ? ' ' : ''}between ${renderBytes(minSize)} and ${renderBytes(maxSize)}`;
  } else if (minSize) {
    caption += `${caption ? ' ' : ''}at least ${renderBytes(minSize)}`;
  } else if (maxSize) {
    caption += `${caption ? ' ' : ''}less than ${renderBytes(maxSize)}`;
  }

  return (
    <div className={cn('flex flex-col items-center justify-center', className)}>
      <div className='flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground'>
        <UploadIcon size={16} />
      </div>
      <p className='my-2 w-full truncate text-sm font-medium'>
        Upload {maxFiles === 1 ? 'a file' : 'files'}
      </p>
      <p className='w-full truncate text-xs text-muted-foreground'>
        Drag and drop or click to upload
      </p>
      {caption && <p className='text-xs text-muted-foreground'>{caption}.</p>}
    </div>
  );
};

export type DropzoneContentProps = {
  children?: React.ReactNode;
  className?: string;
};

const MAX_LABEL_ITEMS = 3;

export const DropzoneContent: React.FC<DropzoneContentProps> = ({ children, className }) => {
  const { src } = useDropzoneContext();
  if (!src || src.length === 0) {
    return null;
  }

  if (children) {
    return children;
  } // custom override

  const names = src.map(f => f.name);
  const label =
    names.length > MAX_LABEL_ITEMS
      ? `${new Intl.ListFormat('en').format(names.slice(0, MAX_LABEL_ITEMS))} and ${
          names.length - MAX_LABEL_ITEMS
        } more`
      : new Intl.ListFormat('en').format(names);

  return (
    <div className={cn('flex flex-col items-center justify-center', className)}>
      <div className='flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground'>
        <UploadIcon size={16} />
      </div>
      <p className='my-2 w-full truncate text-sm font-medium'>{label}</p>
      <p className='w-full text-xs text-muted-foreground'>Drag and drop or click to replace</p>
    </div>
  );
};

export default Dropzone;
